/**
 * Format a past timestamp as a coarse relative-time label, capped at days.
 *
 * Examples (with default `now`):
 *   formatRelativeTime(Date.now() - 30_000)        // "Just now"
 *   formatRelativeTime(Date.now() - 5 * 60 * 1000) // "5 min ago"
 *   formatRelativeTime(Date.now() - 3 * 3600_000)  // "3 h ago"
 *   formatRelativeTime(Date.now() - 2 * 86400_000) // "2 days ago"
 *
 * The maximum granularity is days. Notifications self-evict after 30 days, so
 * units larger than days (weeks / months / years) would be dead code in the
 * surface this is currently used by — and reading "29 days ago" is more honest
 * than rounding to "4 weeks ago".
 *
 * `now` is overridable so the function is deterministic in tests.
 *
 * Future timestamps (clock skew) return "Just now" — we don't render
 * negative durations.
 */
export function formatRelativeTime(timestamp: number, now: number = Date.now()): string {
  const diffMs = now - timestamp;
  if (diffMs < MINUTE_MS) return 'Just now';
  if (diffMs < HOUR_MS) {
    const mins = Math.floor(diffMs / MINUTE_MS);
    return `${mins} min ago`;
  }
  if (diffMs < DAY_MS) {
    const hours = Math.floor(diffMs / HOUR_MS);
    return `${hours} h ago`;
  }
  const days = Math.floor(diffMs / DAY_MS);
  return `${days} ${days === 1 ? 'day' : 'days'} ago`;
}

/**
 * Format a deadline as the time left before it, or "Expired" once it has passed. The mirror of
 * `formatRelativeTime`: same units, rounded down, so "in 3 days" never promises a day that
 * isn't there.
 *
 *   formatExpiresIn(Date.now() + 4.5 * 86400_000) // "in 4 days"
 *   formatExpiresIn(Date.now() - 1)               // "Expired"
 */
export function formatExpiresIn(expiresAt: number, now: number = Date.now()): string {
  const diffMs = expiresAt - now;
  if (diffMs <= 0) return 'Expired';
  if (diffMs < MINUTE_MS) return 'in under a minute';
  if (diffMs < HOUR_MS) return `in ${Math.floor(diffMs / MINUTE_MS)} min`;
  if (diffMs < DAY_MS) return `in ${Math.floor(diffMs / HOUR_MS)} h`;
  const days = Math.floor(diffMs / DAY_MS);
  return `in ${days} ${days === 1 ? 'day' : 'days'}`;
}

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * Past this age, `formatTimeAgoOrDate` switches to an absolute date. 30 days is
 * where `formatRelativeTime`'s own reasoning runs out: it was written for
 * notifications that self-evict at 30 days, so "29 days ago" is the oldest label
 * it was ever designed to produce.
 */
const RELATIVE_HORIZON_DAYS = 30;

/**
 * Absolute date label, e.g. "Jun 12, 2026".
 *
 * Matches the format the Library table's date cells already use, so relative and
 * absolute dates elsewhere in the app read as one system.
 */
export function formatAbsoluteDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Relative for recent timestamps, absolute once they pass the horizon.
 *
 * For long-lived records — libraries, which never self-evict — where
 * `formatRelativeTime` alone would emit useless labels like "243 days ago" and a
 * calendar date is what the reader actually wants.
 *
 * `now` is overridable so the function is deterministic in tests.
 */
export function formatTimeAgoOrDate(timestamp: number, now: number = Date.now()): string {
  return now - timestamp < RELATIVE_HORIZON_DAYS * DAY_MS
    ? formatRelativeTime(timestamp, now)
    : formatAbsoluteDate(timestamp);
}
